import React, { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import GlassCard from "@/components/common/GlassCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { bandColor } from "./seoUtils";

const COLS = [
  { key: "page", label: "Page" },
  { key: "url", label: "URL" },
  { key: "type", label: "Type" },
  { key: "score", label: "Score" },
  { key: "status", label: "Status" },
  { key: "crawl", label: "Last crawl" },
  { key: "actions", label: "Actions" },
];

export default function SEOPageList({
  items, total, loading, query, onQuery, filters, onFilter, onOpen, onAdd, onDiscover,
  selected, onToggle, onBulkAnalyze, onBulkPublish, onPublishCore, onPublishAllDrafts, onPublishBlog, onSelectAllDrafts, draftCount, onAnalyzeOne, onExport, canPublish,
}) {
  const allIds = useMemo(() => (items || []).map((p) => p.id), [items]);
  const [visible, setVisible] = useState(() => Object.fromEntries(COLS.map((c) => [c.key, true])));
  const show = (key) => visible[key] !== false;
  const empty = !loading && (!items || items.length === 0);
  return (
    <GlassCard className="p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-white/5 text-xs text-white/50">
        Draft ≠ live: Save keeps changes private. Publish writes title, description, robots, and schema to the public SEO API.
      </div>
      <div className="flex flex-wrap gap-2 p-4 border-b border-white/5">
        <Input value={query} onChange={(e) => onQuery(e.target.value)} placeholder="Search URL or name" className="bg-black/20 border-white/10 text-white max-w-xs" />
        <select value={filters.page_type} onChange={(e) => onFilter("page_type", e.target.value)} className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2">
          <option value="">All types</option>
          {["landing", "service", "portfolio", "blog", "about", "contact", "custom"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => onFilter("source", e.target.value)} className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2">
          <option value="">All sources</option>
          {["cms", "manual", "crawler", "sitemap"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filters.status || ""} onChange={(e) => onFilter("status", e.target.value)} className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2">
          <option value="">All status</option>
          <option value="draft">draft</option>
          <option value="published">published</option>
        </select>
        <Button onClick={onAdd} className="btn-gradient text-white border-0 ml-auto"><Icons.Plus className="w-4 h-4 mr-1" /> Add URL</Button>
        <Button variant="ghost" onClick={onDiscover} className="text-white/70 hover:text-white">Discover</Button>
        <Button variant="ghost" onClick={onBulkAnalyze} disabled={!selected.size} className="text-white/70 hover:text-white disabled:opacity-40">Analyze selected</Button>
        <Button variant="ghost" onClick={onBulkPublish} disabled={!selected.size || !canPublish} className="text-white/70 hover:text-white disabled:opacity-40" title={!canPublish ? "Publishing requires SEO Manager or Admin" : undefined}>Publish selected</Button>
        {canPublish && (
          <>
            <Button variant="ghost" onClick={onSelectAllDrafts} disabled={!draftCount} className="text-white/70 hover:text-white disabled:opacity-40">Select drafts</Button>
            <Button variant="ghost" onClick={onPublishAllDrafts} disabled={!draftCount} className="text-white/70 hover:text-white disabled:opacity-40">Publish all drafts</Button>
            <Button variant="ghost" onClick={onPublishCore} className="text-white/70 hover:text-white">Publish core</Button>
            <Button variant="ghost" onClick={onPublishBlog} className="text-white/70 hover:text-white">Publish blog</Button>
          </>
        )}
        <Button variant="ghost" onClick={onExport} className="text-white/70 hover:text-white">Export CSV</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-white/40 bg-white/[0.02]">
            <tr>
              <th className="px-4 py-2"><input type="checkbox" aria-label="Select all" onChange={(e) => allIds.forEach((id) => onToggle(id, e.target.checked))} /></th>
              {show("page") && <th className="px-4 py-2 text-left">Page</th>}
              {show("url") && <th className="px-4 py-2 text-left">URL</th>}
              {show("type") && <th className="px-4 py-2 text-left">Type</th>}
              {show("score") && <th className="px-4 py-2 text-left">Score</th>}
              {show("status") && <th className="px-4 py-2 text-left">Status</th>}
              {show("crawl") && <th className="px-4 py-2 text-left">Last crawl</th>}
              {show("actions") && <th className="px-4 py-2 text-left">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="px-4 py-10 text-center text-white/40">Loading pages…</td></tr> : null}
            {!loading && (items || []).map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3"><input type="checkbox" checked={selected.has(p.id)} onChange={(e) => onToggle(p.id, e.target.checked)} aria-label={`Select ${p.name}`} /></td>
                {show("page") && <td className="px-4 py-3"><button className="text-white text-left" onClick={() => onOpen(p)}>{p.name}</button>{p.needs_reanalysis && <span className="ml-2 text-[10px] text-amber-300">Needs re-analysis</span>}</td>}
                {show("url") && <td className="px-4 py-3 font-mono text-xs text-white/50">{p.path}</td>}
                {show("type") && <td className="px-4 py-3 text-white/60">{p.page_type}</td>}
                {show("score") && <td className="px-4 py-3 font-medium" style={{ color: bandColor(p.seo_score) }}>{p.seo_score ?? "—"}</td>}
                {show("status") && (
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${(p.publish_state || p.status) === "published" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-white/50"}`}>
                      {p.publish_state || p.status || "draft"}
                    </span>
                  </td>
                )}
                {show("crawl") && <td className="px-4 py-3 text-white/40 text-xs">{p.last_crawled ? p.last_crawled.slice(0, 16).replace("T", " ") : "Never"}</td>}
                {show("actions") && (
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button type="button" className="text-xs text-white/50 hover:text-white" onClick={() => onAnalyzeOne?.(p)}>Analyze</button>
                      <button type="button" className="text-xs text-cyan-300/80 hover:text-cyan-200" onClick={() => onOpen(p)}>Edit</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {empty && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <p className="text-white/50 mb-4">No SEO pages yet. Discover the website or add a URL to start.</p>
                  <div className="flex justify-center gap-2">
                    <Button onClick={onDiscover} className="btn-gradient text-white border-0">Discover URLs</Button>
                    <Button variant="ghost" onClick={onAdd} className="text-white/70">Add URL</Button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 text-xs text-white/40">{total || 0} URLs · {selected.size} selected</div>
    </GlassCard>
  );
}
